/**
 * hero-home-v2.js
 * ---------------
 * Controla el hero de marca cambiante: sincroniza el carrusel de tarjetas con
 * el fondo, el PNG de la mujer, el logo, el título y los CTAs.
 *
 * Carrusel: Swiper (cargado global en layout/theme.liquid).
 * Si Swiper no está disponible cae a un track con scroll-snap nativo, así la
 * sección nunca queda inutilizable.
 */
(function () {
  'use strict';

  // El <script> vive dentro de la sección: si la sección se usa dos veces en
  // la página el navegador ejecutaría el archivo dos veces.
  if (window.__heroHomeV2Loaded) return;
  window.__heroHomeV2Loaded = true;

  var LOG = '[hero-home-v2]';
  var instances = new Map();

  function HeroHomeV2(root) {
    this.root = root;
    this.panels = Array.prototype.slice.call(
      root.querySelectorAll('[data-hh2-panel]')
    );
    this.backgrounds = Array.prototype.slice.call(
      root.querySelectorAll('[data-hh2-bg]')
    );
    this.figures = Array.prototype.slice.call(
      root.querySelectorAll('[data-hh2-figure]')
    );
    this.swiperEl = root.querySelector('[data-hh2-swiper]');
    this.trackEl = root.querySelector('[data-hh2-track]');
    this.prevEl = root.querySelector('[data-hh2-prev]');
    this.nextEl = root.querySelector('[data-hh2-next]');

    this.total = this.panels.length;
    this.index = 0;
    this.swiper = null;
    this.fallbackTimer = null;

    this.autoplay = root.dataset.autoplay === 'true';
    this.autoplayDelay = parseInt(root.dataset.autoplayDelay, 10) || 6000;
    this.gap = parseInt(root.dataset.gap, 10) || 14;
    this.reducedMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.onSelectClick = this.onSelectClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.init();
  }

  HeroHomeV2.prototype.init = function () {
    if (!this.total) {
      console.log(LOG, 'sin slides, nada que inicializar');
      return;
    }

    if (this.swiperEl) {
      this.swiperEl.addEventListener('click', this.onSelectClick);
      this.swiperEl.addEventListener('keydown', this.onKeydown);
    }

    if (typeof window.Swiper === 'function') {
      this.initSwiper();
    } else {
      console.log(LOG, 'Swiper no disponible, usando fallback scroll-snap');
      this.initFallback();
    }

    this.setActive(0, true);
  };

  /* ---------------------------------------------------------------- Swiper */

  HeroHomeV2.prototype.initSwiper = function () {
    var self = this;

    // El markup repite el set de tarjetas, así que hay más slides que paneles.
    this.slideCount = this.trackEl
      ? this.trackEl.querySelectorAll('[data-hh2-slide]').length
      : this.total;
    // Arrancamos en la copia central para tener tarjetas de sobra a ambos lados.
    this.anchor =
      Math.floor(this.slideCount / this.total / 2) * this.total;

    var options = {
      slidesPerView: 'auto',
      centeredSlides: true,
      // El CSS reduce el gap un 30% en móvil; lo replicamos para que Swiper
      // calcule las posiciones con la misma separación.
      spaceBetween: Math.round(this.gap * 0.7),
      breakpoints: {
        750: { spaceBetween: this.gap }
      },
      speed: this.reducedMotion ? 1 : 600,
      grabCursor: true,
      watchSlidesProgress: true,
      initialSlide: this.anchor,
      // Sin loop nativo: el infinito lo da el set repetido + rebase().
      loop: false,
      keyboard: { enabled: true, onlyInViewport: true },
      a11y: {
        enabled: true,
        prevSlideMessage: 'Slide anterior',
        nextSlideMessage: 'Slide siguiente'
      },
      autoplay:
        this.autoplay && !this.reducedMotion
          ? {
              delay: this.autoplayDelay,
              disableOnInteraction: false,
              pauseOnMouseEnter: true
            }
          : false,
      on: {
        slideChange: function () {
          self.setActive(this.activeIndex % self.total);
        },
        transitionEnd: function () {
          self.rebase();
        }
      }
    };

    if (this.prevEl && this.nextEl) {
      options.navigation = { prevEl: this.prevEl, nextEl: this.nextEl };
    }

    this.swiper = new window.Swiper(this.swiperEl, options);
    console.log(LOG, 'Swiper inicializado', {
      paneles: this.total,
      tarjetas: this.slideCount,
      ancla: this.anchor,
      autoplay: !!options.autoplay
    });
  };

  /**
   * Si la posición se acercó a un extremo del track, salta a la tarjeta
   * equivalente de la copia central sin animación. Es invisible porque las
   * copias son idénticas, y evita quedarse sin tarjetas a los costados.
   */
  HeroHomeV2.prototype.rebase = function () {
    if (!this.swiper) return;
    var i = this.swiper.activeIndex;
    var min = this.total;
    var max = this.slideCount - this.total - 1;
    if (i >= min && i <= max) return;

    var target = this.anchor + (i % this.total);
    this.swiper.slideTo(target, 0, false);
  };

  /**
   * Traduce un índice lógico (0..total-1) a la repetición más cercana del track.
   */
  HeroHomeV2.prototype.nearestSlideFor = function (logicalIndex) {
    var current = this.swiper.activeIndex;
    var best = logicalIndex;
    var bestDistance = Infinity;

    for (var i = logicalIndex; i < this.slideCount; i += this.total) {
      var distance = Math.abs(i - current);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    }

    return best;
  };

  /* -------------------------------------------------------------- Fallback */

  HeroHomeV2.prototype.initFallback = function () {
    this.root.classList.add('hh2--no-swiper');

    if (this.autoplay && !this.reducedMotion) {
      this.startFallbackTimer();

      var self = this;
      this.root.addEventListener('mouseenter', function () {
        self.stopFallbackTimer();
      });
      this.root.addEventListener('mouseleave', function () {
        self.startFallbackTimer();
      });
      this.root.addEventListener('focusin', function () {
        self.stopFallbackTimer();
      });
    }

    if (this.prevEl) {
      var self2 = this;
      this.prevEl.addEventListener('click', function () {
        self2.goTo((self2.index - 1 + self2.total) % self2.total);
      });
    }
    if (this.nextEl) {
      var self3 = this;
      this.nextEl.addEventListener('click', function () {
        self3.goTo((self3.index + 1) % self3.total);
      });
    }
  };

  HeroHomeV2.prototype.startFallbackTimer = function () {
    var self = this;
    this.stopFallbackTimer();
    this.fallbackTimer = window.setInterval(function () {
      self.goTo((self.index + 1) % self.total);
    }, this.autoplayDelay);
  };

  HeroHomeV2.prototype.stopFallbackTimer = function () {
    if (this.fallbackTimer) {
      window.clearInterval(this.fallbackTimer);
      this.fallbackTimer = null;
    }
  };

  /* ------------------------------------------------------------ Navegación */

  HeroHomeV2.prototype.onSelectClick = function (event) {
    var hit = event.target.closest('[data-hh2-select]');
    if (!hit) return;
    var index = parseInt(hit.getAttribute('data-hh2-select'), 10);
    if (isNaN(index)) return;
    this.goTo(index);
  };

  HeroHomeV2.prototype.onKeydown = function (event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (!event.target.closest('[data-hh2-select]')) return;
    event.preventDefault();
    var delta = event.key === 'ArrowRight' ? 1 : -1;
    this.goTo((this.index + delta + this.total) % this.total);
  };

  /** Mueve el carrusel al índice dado (el estado visual lo aplica setActive). */
  HeroHomeV2.prototype.goTo = function (index) {
    if (index === this.index && this.swiper) return;

    if (this.swiper) {
      this.swiper.slideTo(this.nearestSlideFor(index));
      return;
    }

    // Fallback: centrar la tarjeta a mano
    this.setActive(index);
    var slide = this.trackEl
      ? this.trackEl.querySelector('[data-hh2-slide="' + index + '"]')
      : null;
    if (slide && this.swiperEl) {
      var offset =
        slide.offsetLeft - (this.swiperEl.clientWidth - slide.offsetWidth) / 2;
      this.swiperEl.scrollTo({
        left: offset,
        behavior: this.reducedMotion ? 'auto' : 'smooth'
      });
    }
  };

  /* ------------------------------------------------------ Estado visual */

  HeroHomeV2.prototype.setActive = function (index, force) {
    if (!force && index === this.index) return;
    this.index = index;

    this.panels.forEach(function (panel, i) {
      var active = i === index;
      panel.classList.toggle('is-active', active);
      if (active) {
        panel.removeAttribute('inert');
        panel.removeAttribute('aria-hidden');
      } else {
        panel.setAttribute('inert', '');
        panel.setAttribute('aria-hidden', 'true');
      }
    });

    this.backgrounds.forEach(function (bg, i) {
      bg.classList.toggle('is-active', i === index);
    });

    this.figures.forEach(function (fig, i) {
      fig.classList.toggle('is-active', i === index);
    });

    // aria-current solo en la primera repetición: las copias están fuera del
    // árbol de accesibilidad y marcarlas duplicaría el anuncio.
    var hits = this.root.querySelectorAll(
      '[data-hh2-copy="0"] [data-hh2-select]'
    );
    if (!hits.length) hits = this.root.querySelectorAll('[data-hh2-select]');
    Array.prototype.forEach.call(hits, function (hit) {
      var i = parseInt(hit.getAttribute('data-hh2-select'), 10);
      if (i === index) {
        hit.setAttribute('aria-current', 'true');
      } else {
        hit.removeAttribute('aria-current');
      }
    });

    // En el fallback no hay .swiper-slide-active, lo marcamos nosotros
    if (!this.swiper && this.trackEl) {
      var slides = this.trackEl.querySelectorAll('[data-hh2-slide]');
      Array.prototype.forEach.call(slides, function (slide) {
        var i = parseInt(slide.getAttribute('data-hh2-slide'), 10);
        slide.classList.toggle('is-active', i === index);
      });
    }
  };

  /* ------------------------------------------------------------ Autoplay */

  HeroHomeV2.prototype.pause = function () {
    if (this.swiper && this.swiper.autoplay) this.swiper.autoplay.stop();
    this.stopFallbackTimer();
  };

  HeroHomeV2.prototype.resume = function () {
    if (!this.autoplay || this.reducedMotion) return;
    if (this.swiper && this.swiper.autoplay) this.swiper.autoplay.start();
    else this.startFallbackTimer();
  };

  HeroHomeV2.prototype.destroy = function () {
    this.pause();
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    if (this.swiperEl) {
      this.swiperEl.removeEventListener('click', this.onSelectClick);
      this.swiperEl.removeEventListener('keydown', this.onKeydown);
    }
  };

  /* -------------------------------------------------------------- Arranque */

  function mount(scope) {
    var roots = (scope || document).querySelectorAll('[data-hh2]');
    Array.prototype.forEach.call(roots, function (root) {
      if (instances.has(root)) return;
      instances.set(root, new HeroHomeV2(root));
    });
  }

  function findInstance(target) {
    var root = target && target.querySelector && target.querySelector('[data-hh2]');
    if (!root && target && target.matches && target.matches('[data-hh2]')) {
      root = target;
    }
    return root ? instances.get(root) : null;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      mount();
    });
  } else {
    mount();
  }

  /* Theme editor de Shopify */
  document.addEventListener('shopify:section:load', function (event) {
    mount(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.querySelector('[data-hh2]');
    if (!root) return;
    var instance = instances.get(root);
    if (instance) instance.destroy();
    instances.delete(root);
  });

  document.addEventListener('shopify:block:select', function (event) {
    var root = event.target.closest('[data-hh2]');
    if (!root) return;
    var instance = instances.get(root);
    if (!instance) return;
    var slide = event.target.closest('[data-hh2-slide]');
    var index = slide
      ? parseInt(slide.getAttribute('data-hh2-slide'), 10)
      : parseInt(event.target.getAttribute('data-hh2-panel'), 10);
    instance.pause();
    if (!isNaN(index)) instance.goTo(index);
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    var root = event.target.closest('[data-hh2]');
    if (!root) return;
    var instance = instances.get(root);
    if (instance) instance.resume();
  });

  document.addEventListener('shopify:section:select', function (event) {
    var instance = findInstance(event.target);
    if (instance) instance.pause();
  });

  document.addEventListener('shopify:section:deselect', function (event) {
    var instance = findInstance(event.target);
    if (instance) instance.resume();
  });
})();
